import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LANGS } from "@/i18n";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "platform", label: t.nav.platform },
    { id: "events", label: t.nav.events },
    { id: "volunteer", label: t.nav.volunteer },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button
          data-testid="logo-btn"
          onClick={() => scrollTo("hero")}
          className="font-display text-xl md:text-2xl tracking-wide uppercase"
        >
          Neil<span className="text-purple-500">.</span>Simon
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="text-sm uppercase tracking-widest text-white/60 hover:text-white transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              data-testid="lang-switcher"
              aria-label="Change language"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/15 text-sm hover:border-purple-500 transition-colors"
            >
              <Globe size={15} />
              <span className="uppercase tracking-wider">{lang}</span>
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.ul
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  className="absolute right-0 mt-2 w-32 glass rounded-xl overflow-hidden py-1"
                >
                  {LANGS.map((l) => (
                    <li key={l.code}>
                      <button
                        data-testid={`lang-option-${l.code}`}
                        onClick={() => { setLang(l.code); setLangOpen(false); }}
                        className="w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-white/10 transition-colors"
                      >
                        {l.label}
                        {lang === l.code && <Check size={14} className="text-purple-400" />}
                      </button>
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>

          <button
            data-testid="nav-donate"
            onClick={() => scrollTo("donate")}
            className="hidden sm:inline-flex items-center px-5 py-2.5 rounded-full bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold tracking-wide transition-colors"
          >
            {t.nav.donate}
          </button>

          <button
            data-testid="mobile-menu-btn"
            aria-label="Menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {[...links, { id: "donate", label: t.nav.donate }].map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => { scrollTo(l.id); setOpen(false); }}
                  className="text-left text-lg uppercase tracking-wide text-white/80"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
